import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Row Types (SQLite) ───

export interface InvoiceRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  appointment_id: string | null;
  consultation_id: string | null;
  queue_id: string | null;
  encounter_id: string | null;
  visit_id: string | null;
  invoice_no: string | null;
  patient_name: string | null;
  date_iso: string | null;
  status: string | null; // 'draft' | 'issued' | 'paid'
  reference_no: string | null;
  reference_type: string | null; // 'official' | 'unofficial'
  notes: string | null;
  subtotal: number | null;
  discount_total: number | null;
  tax_total: number | null;
  grand_total: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface InvoiceItemRow {
  id: string;
  org_id: string | null;
  invoice_id: string | null;
  service_name: string | null;
  service_id: string | null;
  qty: number | null;
  unit_price: number | null;
  discount: number | null;
  /** PRD.228 — 'fixed' | 'percentage'. Older rows default to 'fixed'. */
  discount_type: string | null;
  tax_rate: number | null;
  // PRD.190 — optional Medicine inventory reference + snapshots
  inventory_item_id: string | null;
  free_text_name: string | null;
  description_snapshot: string | null;
  sku_snapshot: string | null;
  unit_snapshot: string | null;
  unit_price_snapshot: number | null;
  tax_inclusive_snapshot: number | null; // 0/1
  open_pricing_snapshot: number | null; // 0/1
  source_type: string | null; // 'manual' | 'inventory_item' | 'prescription' | 'pharmacy_dispense' | 'procedure' | 'encounter'
  source_id: string | null;
  remarks: string | null;
  add_to_treatment: number | null; // 0/1
  sort_order: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface InvoicePaymentRow {
  id: string;
  org_id: string | null;
  /** PRD.229 — null when the payment is patient-level (not tied to a specific invoice). */
  invoice_id: string | null;
  /** PRD.229 — direct patient link for new payments; legacy rows may be null. */
  patient_id: string | null;
  amount_cents: number | null;
  method: string | null;
  notes: string | null;
  /** PRD.244 — optional item-level allocation, JSON array of PaymentAllocation. */
  allocations: string | null;
  paid_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

// ─── PRD.244 — Item-level payment allocation ───
//
// A payment MAY carry an optional breakdown of how it was split across
// invoice items. It lives as JSON on the payment row (invoice_payments.
// allocations) rather than a separate table: allocation is inherently a
// child of the payment, is always read alongside it, and the payment row
// stays the single authoritative record. Invoice-level totals ignore this
// breakdown entirely (they sum amount_cents), so an unallocated payment is
// always valid.

export interface PaymentAllocation {
  /** invoice_items.id this slice of the payment was applied to. */
  item_id: string;
  amount_cents: number;
}

/** Safely parse the invoice_payments.allocations JSON into typed rows. */
export function parsePaymentAllocations(json: string | null | undefined): PaymentAllocation[] {
  if (!json) return [];
  try {
    const raw = JSON.parse(json);
    if (!Array.isArray(raw)) return [];
    return raw
      .filter(
        (a): a is PaymentAllocation =>
          a != null && typeof a.item_id === 'string' && Number.isFinite(a.amount_cents),
      )
      .map((a) => ({ item_id: a.item_id, amount_cents: Math.round(a.amount_cents) }));
  } catch {
    return [];
  }
}

/**
 * Aggregate a set of payments into per-item paid amounts and an unallocated
 * remainder, for showing item-level paid/outstanding.
 *
 * - `allocatedByItem` sums explicit allocations per item across all payments.
 * - `totalPaidCents` is the invoice's authoritative paid total (sum of
 *   amount_cents) — the source of truth.
 * - `unallocatedCents` is whatever paid amount was NOT tied to an item
 *   (totalPaid − sum of allocations that point at a known item). Per PRD.244
 *   §5.5 this still counts toward the invoice; the UI may spread it pro-rata
 *   as a clearly-labeled estimate.
 */
export function aggregatePaymentAllocations(
  payments: ReadonlyArray<{ amount_cents: number | null; allocations: string | null }>,
  knownItemIds: ReadonlySet<string>,
): { allocatedByItem: Map<string, number>; totalPaidCents: number; unallocatedCents: number } {
  const allocatedByItem = new Map<string, number>();
  let totalPaidCents = 0;
  let matchedAllocCents = 0;
  for (const p of payments) {
    totalPaidCents += p.amount_cents ?? 0;
    for (const a of parsePaymentAllocations(p.allocations)) {
      if (!knownItemIds.has(a.item_id)) continue; // allocation to a deleted item → unallocated
      allocatedByItem.set(a.item_id, (allocatedByItem.get(a.item_id) ?? 0) + a.amount_cents);
      matchedAllocCents += a.amount_cents;
    }
  }
  const unallocatedCents = Math.max(0, totalPaidCents - matchedAllocCents);
  return { allocatedByItem, totalPaidCents, unallocatedCents };
}

// ─── Queries ───

export function qInvoicesByPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['invoices', 'by-patient', patientId] as const,
    sql: `SELECT * FROM invoices WHERE patient_id = ? ORDER BY created_at DESC`,
    params: [patientId],
    watch: ['invoices'],
  };
}

export function qInvoicesByEncounter(
  patientId: string,
  encounterId: string,
): QuerySpec<[string, string]> {
  return {
    key: ['invoices', 'by-encounter', patientId, encounterId] as const,
    sql: `SELECT * FROM invoices WHERE patient_id = ? AND encounter_id = ? ORDER BY created_at DESC`,
    params: [patientId, encounterId],
    watch: ['invoices'],
  };
}

/**
 * PRD.229 follow-up — invoices for a patient on a specific visit date,
 * across encounters. Used by the encounter-scoped Invoices tab so that
 * if a patient has multiple encounters on the same day (e.g. morning +
 * afternoon visits), the tab surfaces ALL of that day's invoices, not
 * just the ones linked to the currently-open encounter.
 *
 * `dateIso` is expected as a YYYY-MM-DD string matching invoices.date_iso.
 */
export function qInvoicesByPatientAndDate(
  patientId: string,
  dateIso: string,
): QuerySpec<[string, string]> {
  return {
    key: ['invoices', 'by-patient-date', patientId, dateIso] as const,
    sql: `SELECT * FROM invoices
            WHERE patient_id = ? AND date_iso = ?
            ORDER BY created_at DESC`,
    params: [patientId, dateIso],
    watch: ['invoices'],
  };
}

// ─── Patient billing aggregate ─────────────────────────────────────────────
//
// PRD.218 — Single source of truth for patient-scoped charged / paid /
// balance. Powers both the Patient Overview billing card and the
// sidebar balance chip so neither surface re-derives money math.
//
// Money values are returned in minor units (cents) so callers can wrap
// them in Dinero without rounding ambiguity. `balance_cents` is
// computed in SQL as `charged - paid` so a partially-paid invoice
// contributes its remainder, not its full grand_total.
//
// `unpaid_count` and `has_overdue` consider only invoices that still
// have positive remaining AND are not already flipped to 'paid' (defense
// against legacy 'paid' rows that have no matching payments).

export interface PatientBillingAggregateRow {
  charged_cents: number;
  paid_cents: number;
  /** PRD.227 — Sum of debit adjustments (increases balance). */
  debit_cents: number;
  /** PRD.227 — Sum of credit adjustments (decreases balance). */
  credit_cents: number;
  /** PRD.227 §8.2 — Balance = Charges + Debits − Payments − Credits. */
  balance_cents: number;
  unpaid_count: number;
  has_overdue: number; // 1 if any unpaid invoice > 30 days old
}

export function qPatientBillingAggregate(patientId: string): QuerySpec<[string, string, string]> {
  return {
    key: ['invoices', 'patient-billing', patientId] as const,
    // Two-pass aggregate per PRD.227 §8.2:
    //   Balance = Charges + Debits − Payments − Credits
    // `inv_totals` collapses invoice charges + payment subtotals; `adj_totals`
    // splits billing_adjustments into debit/credit sums. CROSS JOIN of two
    // single-row CTEs always returns exactly one row (SUM over an empty set
    // is NULL → COALESCE to 0).
    sql: `
      WITH
      inv AS (
        SELECT
          id,
          status,
          created_at,
          CAST(ROUND(COALESCE(grand_total, 0) * 100) AS INTEGER) AS grand_cents
        FROM invoices
        WHERE patient_id = ?
      ),
      paid AS (
        SELECT
          invoice_id,
          COALESCE(SUM(amount_cents), 0) AS paid_cents
        FROM invoice_payments
        WHERE invoice_id IS NOT NULL
        GROUP BY invoice_id
      ),
      inv_totals AS (
        SELECT
          COALESCE(SUM(inv.grand_cents), 0)                              AS charged_cents,
          COALESCE(SUM(COALESCE(paid.paid_cents, 0)), 0)                 AS invoice_paid_cents,
          SUM(
            CASE WHEN inv.grand_cents - COALESCE(paid.paid_cents, 0) > 0
                  AND inv.status != 'paid'
                 THEN 1 ELSE 0 END
          )                                                              AS unpaid_count,
          COALESCE(MAX(
            CASE WHEN inv.grand_cents - COALESCE(paid.paid_cents, 0) > 0
                  AND inv.status != 'paid'
                  AND julianday('now') - julianday(inv.created_at) > 30
                 THEN 1 ELSE 0 END
          ), 0)                                                          AS has_overdue
        FROM inv
        LEFT JOIN paid ON paid.invoice_id = inv.id
      ),
      -- PRD.229 — patient-level payments (invoice_id IS NULL, patient_id = ?).
      -- We scope by patient_id directly to avoid double-counting any payment
      -- that's already counted via the invoice JOIN above.
      patient_paid AS (
        SELECT
          COALESCE(SUM(amount_cents), 0) AS paid_cents
        FROM invoice_payments
        WHERE invoice_id IS NULL AND patient_id = ?
      ),
      adj_totals AS (
        SELECT
          COALESCE(SUM(CASE WHEN adjustment_type = 'debit'  THEN amount_cents ELSE 0 END), 0) AS debit_cents,
          COALESCE(SUM(CASE WHEN adjustment_type = 'credit' THEN amount_cents ELSE 0 END), 0) AS credit_cents
        FROM billing_adjustments
        WHERE patient_id = ?
      )
      SELECT
        inv_totals.charged_cents,
        (inv_totals.invoice_paid_cents + patient_paid.paid_cents) AS paid_cents,
        adj_totals.debit_cents,
        adj_totals.credit_cents,
        (inv_totals.charged_cents + adj_totals.debit_cents
          - inv_totals.invoice_paid_cents - patient_paid.paid_cents
          - adj_totals.credit_cents) AS balance_cents,
        inv_totals.unpaid_count,
        inv_totals.has_overdue
      FROM inv_totals, patient_paid, adj_totals
    `,
    params: [patientId, patientId, patientId],
    watch: ['invoices', 'invoice_payments', 'billing_adjustments'],
  };
}

export function qInvoiceItems(invoiceId: string): QuerySpec<[string]> {
  return {
    key: ['invoice_items', 'by-invoice', invoiceId] as const,
    sql: `SELECT * FROM invoice_items WHERE invoice_id = ? ORDER BY sort_order ASC, created_at ASC`,
    params: [invoiceId],
    watch: ['invoice_items'],
  };
}

/**
 * PRD.245 follow-up — every invoice line's id + remarks for a patient, so the
 * Treatment Record can reflect the LATEST per-line remark on its linked invoice
 * rows (live, not a stale snapshot). Keyed lookups happen client-side by item id.
 */
export interface InvoiceItemRemarkRow {
  id: string;
  invoice_id: string | null;
  remarks: string | null;
}

export function qInvoiceItemRemarksByPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['invoice_items', 'remarks-by-patient', patientId] as const,
    sql: `SELECT ii.id, ii.invoice_id, ii.remarks
            FROM invoice_items ii
            JOIN invoices i ON i.id = ii.invoice_id
           WHERE i.patient_id = ?`,
    params: [patientId],
    watch: ['invoice_items', 'invoices'],
  };
}

/** Invoice item joined with its parent invoice's number/status. */
export interface PatientInvoiceItemRow extends InvoiceItemRow {
  invoice_no: string | null;
  invoice_status: string | null;
}

/**
 * PRD.247 — every invoice line item for a patient, joined to its invoice so the
 * Billing Item Payment Status table can group items by invoice. Newest invoice
 * first, then the invoice's own item order.
 */
export function qInvoiceItemsByPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['invoice_items', 'by-patient', patientId] as const,
    sql: `SELECT ii.*, i.invoice_no AS invoice_no, i.status AS invoice_status
            FROM invoice_items ii
            JOIN invoices i ON i.id = ii.invoice_id
           WHERE i.patient_id = ?
           ORDER BY i.created_at DESC, ii.sort_order ASC, ii.created_at ASC`,
    params: [patientId],
    watch: ['invoice_items', 'invoices'],
  };
}

// ─── PRD.218 — Invoice Payments V1 ───

/**
 * All payments recorded against an invoice, newest-first.
 * Drives the payment history list and Dinero-based paid/balance math.
 */
export function qInvoicePaymentsByInvoice(invoiceId: string): QuerySpec<[string]> {
  return {
    key: ['invoice_payments', 'by-invoice', invoiceId] as const,
    sql: `SELECT * FROM invoice_payments WHERE invoice_id = ? ORDER BY paid_at DESC, created_at DESC`,
    params: [invoiceId],
    watch: ['invoice_payments'],
  };
}

/**
 * PRD.229 — All payments for a patient (invoice-linked + patient-level),
 * newest-first. Used by the unified billing events table on the patient
 * Billing view. Returns:
 *   - invoice-linked payments: rows where `invoice_id` is set and its
 *     parent invoice belongs to this patient
 *   - patient-level payments: rows where `invoice_id IS NULL` and
 *     `patient_id = ?` (PRD.229 patient-level payment workflow)
 */
export function qPaymentsByPatient(patientId: string): QuerySpec<[string, string]> {
  return {
    key: ['invoice_payments', 'by-patient', patientId] as const,
    sql: `
      SELECT ip.*
      FROM invoice_payments ip
      LEFT JOIN invoices i ON i.id = ip.invoice_id
      WHERE
        (ip.invoice_id IS NOT NULL AND i.patient_id = ?)
        OR (ip.invoice_id IS NULL AND ip.patient_id = ?)
      ORDER BY COALESCE(ip.paid_at, ip.created_at) DESC, ip.created_at DESC
    `,
    params: [patientId, patientId],
    watch: ['invoice_payments', 'invoices'],
  };
}

export interface InvoicePaidTotalRow {
  paid_cents: number;
}

// ─── PRD.219 — Patient Billing Across Visits ───
//
// One row per invoice for a patient with money values in cents and
// paid/balance derived from `invoice_payments` so the table never
// shows stale status-only numbers. Newest-first to match the
// patient timeline.

export interface PatientInvoiceRow {
  id: string;
  invoice_no: string | null;
  date_iso: string | null;
  status: string | null;
  reference_no: string | null;
  reference_type: string | null;
  encounter_id: string | null;
  visit_id: string | null;
  grand_cents: number;
  paid_cents: number;
  balance_cents: number;
  created_at: string | null;
  updated_at: string | null;
}

export function qPatientInvoicesWithPaid(patientId: string): QuerySpec<[string]> {
  return {
    key: ['invoices', 'patient-with-paid', patientId] as const,
    sql: `
      WITH paid AS (
        SELECT
          invoice_id,
          COALESCE(SUM(amount_cents), 0) AS paid_cents
        FROM invoice_payments
        GROUP BY invoice_id
      )
      SELECT
        i.id,
        i.invoice_no,
        i.date_iso,
        i.status,
        i.reference_no,
        i.reference_type,
        i.encounter_id,
        i.visit_id,
        CAST(ROUND(COALESCE(i.grand_total, 0) * 100) AS INTEGER) AS grand_cents,
        COALESCE(paid.paid_cents, 0)                              AS paid_cents,
        CAST(ROUND(COALESCE(i.grand_total, 0) * 100) AS INTEGER) - COALESCE(paid.paid_cents, 0) AS balance_cents,
        i.created_at,
        i.updated_at
      FROM invoices i
      LEFT JOIN paid ON paid.invoice_id = i.id
      WHERE i.patient_id = ?
      ORDER BY COALESCE(i.date_iso, i.created_at) DESC, i.created_at DESC
    `,
    params: [patientId],
    watch: ['invoices', 'invoice_payments'],
  };
}

/**
 * Single-row aggregate of paid amount in raw minor units. Use this
 * when the UI needs a cents integer (e.g. to feed straight into
 * phpFromCents for balance/over-payment validation).
 */
export function qInvoicePaidTotalCents(invoiceId: string): QuerySpec<[string]> {
  return {
    key: ['invoice_payments', 'paid-cents', invoiceId] as const,
    sql: `SELECT COALESCE(SUM(amount_cents), 0) AS paid_cents
            FROM invoice_payments
           WHERE invoice_id = ?`,
    params: [invoiceId],
    watch: ['invoice_payments'],
  };
}

// ─── PRD.216 — Global Invoice list rows ───
//
// Shared row shape for the operational Global Invoice module. Joins
// patient name (preferring the live patients.full_name when the
// denormalised invoices.patient_name is missing/empty) and derives
// paid_total + balance from invoice_payments so the queue can show
// remaining balance without a per-row follow-up query.

export interface GlobalInvoiceListRow {
  id: string;
  invoice_no: string | null;
  date_iso: string | null;
  status: string | null; // 'draft' | 'issued' | 'paid'
  reference_no: string | null;
  reference_type: string | null;
  patient_id: string | null;
  patient_name: string | null;
  encounter_id: string | null;
  grand_total: number | null;
  paid_total: number | null;
  balance: number | null;
  created_at: string | null;
  updated_at: string | null;
}

const GLOBAL_INVOICE_BASE_SELECT = `
  SELECT
    i.id,
    i.invoice_no,
    i.date_iso,
    i.status,
    i.reference_no,
    i.reference_type,
    i.patient_id,
    COALESCE(NULLIF(TRIM(i.patient_name), ''), p.full_name) AS patient_name,
    i.encounter_id,
    i.grand_total,
    COALESCE(
      (SELECT SUM(ip.amount_cents) / 100.0
         FROM invoice_payments ip
        WHERE ip.invoice_id = i.id),
      0
    ) AS paid_total,
    COALESCE(i.grand_total, 0) - COALESCE(
      (SELECT SUM(ip.amount_cents) / 100.0
         FROM invoice_payments ip
        WHERE ip.invoice_id = i.id),
      0
    ) AS balance,
    i.created_at,
    i.updated_at
  FROM invoices i
  LEFT JOIN patients p ON p.id = i.patient_id
`;

/**
 * PRD.216 — Ready to Settle queue (default tab on Global Invoice).
 *
 * Inclusion rule for v1: any invoice that is not yet 'paid'. Ordered
 * oldest-first so frontdesk works through aging balances naturally.
 * Cancelled/voided invoices (when introduced) should be excluded here.
 */
export function qInvoicesReadyToSettle(): QuerySpec<[]> {
  return {
    key: ['global-invoices', 'ready-to-settle'] as const,
    sql: `${GLOBAL_INVOICE_BASE_SELECT}
      WHERE i.status IN ('draft', 'issued')
      ORDER BY i.created_at ASC`,
    params: [],
    watch: ['invoices', 'patients', 'invoice_payments'],
  };
}

/**
 * PRD.216 — Invoice History (secondary tab on Global Invoice).
 *
 * All invoices ordered newest-first. v1 caps at 500 rows; the toolbar
 * search/filters narrow the displayed set. Pagination can come later.
 */
export function qGlobalInvoiceHistory(): QuerySpec<[]> {
  return {
    key: ['global-invoices', 'history'] as const,
    sql: `${GLOBAL_INVOICE_BASE_SELECT}
      ORDER BY i.created_at DESC
      LIMIT 500`,
    params: [],
    watch: ['invoices', 'patients', 'invoice_payments'],
  };
}

/**
 * PRD.216 — Operational KPIs for the Global Invoice queue strip.
 *
 * Returns aggregates the home Billing Snapshot doesn't surface:
 *   - Overdue: status IN ('draft','issued') older than 30 days. Same
 *     >30-day cutoff used by qPatientBillingAggregate for parity.
 *   - Drafts: status = 'draft' regardless of age. Identifies invoices
 *     stuck before issue/payment.
 *
 * One row per query (single SELECT with conditional aggregates) so
 * all metrics share a single watcher and refresh together.
 */
export interface GlobalInvoiceOperationalKpisRow {
  overdue_total: number;
  overdue_count: number;
  draft_total: number;
  draft_count: number;
}

export function qGlobalInvoiceOperationalKpis(): QuerySpec<[]> {
  return {
    key: ['global-invoices', 'operational-kpis'] as const,
    sql: `SELECT
      COALESCE(SUM(CASE
        WHEN status IN ('draft', 'issued')
          AND julianday('now') - julianday(created_at) > 30
        THEN grand_total ELSE 0 END), 0) AS overdue_total,
      SUM(CASE
        WHEN status IN ('draft', 'issued')
          AND julianday('now') - julianday(created_at) > 30
        THEN 1 ELSE 0 END) AS overdue_count,
      COALESCE(SUM(CASE WHEN status = 'draft' THEN grand_total ELSE 0 END), 0) AS draft_total,
      SUM(CASE WHEN status = 'draft' THEN 1 ELSE 0 END) AS draft_count
    FROM invoices`,
    params: [],
    watch: ['invoices'],
  };
}

// ─── PRD.236 — Queue Pay/Paid state ─────────────────────────────────────────
//
// Per-patient billing settlement snapshot for visits on a given date. The
// Queue Pay/Paid action is driven by real invoice/payment state rather than
// a manual status flip, so we aggregate every invoice attached to that
// patient's visit(s) on the queue date and compare paid against grand_total.
//
// `invoice_count` lets the UI distinguish "no billing yet" (count = 0 →
// still Pay) from "fully settled" (count > 0). `item_count` is needed on
// top of that because an invoice header can exist with zero line items
// (e.g. created from Queue but never edited): grand_total = 0 and
// balance = 0 would otherwise wrongly read as Paid. Paid requires at
// least one actual billable line.

export interface VisitSettlementByPatientRow {
  patient_id: string;
  invoice_count: number;
  item_count: number;
  total_balance_cents: number;
}

export function qVisitSettlementByDate(date: string): QuerySpec<[string]> {
  return {
    key: ['visits', 'settlement-by-date', date] as const,
    sql: `
      WITH paid AS (
        SELECT invoice_id, COALESCE(SUM(amount_cents), 0) AS paid_cents
        FROM invoice_payments
        GROUP BY invoice_id
      ),
      items AS (
        SELECT invoice_id, COUNT(*) AS item_count
        FROM invoice_items
        GROUP BY invoice_id
      )
      SELECT
        v.patient_id,
        COUNT(i.id) AS invoice_count,
        COALESCE(SUM(COALESCE(items.item_count, 0)), 0) AS item_count,
        COALESCE(SUM(
          CAST(ROUND(COALESCE(i.grand_total, 0) * 100) AS INTEGER)
          - COALESCE(paid.paid_cents, 0)
        ), 0) AS total_balance_cents
      FROM patient_visits v
      LEFT JOIN invoices i ON i.visit_id = v.id
      LEFT JOIN paid ON paid.invoice_id = i.id
      LEFT JOIN items ON items.invoice_id = i.id
      WHERE v.visit_date = ?
      GROUP BY v.patient_id
    `,
    params: [date],
    watch: ['patient_visits', 'invoices', 'invoice_items', 'invoice_payments'],
  };
}
